/*
  Page indicator for the two Featured collection sliders on the homepage
  (screens 5/7 — section_collection_Yb3qhH, section_collection_4kUEtd).

  The theme's <grid-slider> custom element (assets/theme.js) only builds
  prev/next arrow buttons; it has no page-count concept at all. This
  builds a "01 / 03" style indicator per instance, counting SCREENS
  (products / cards-per-screen), not raw product count.

  State-driven, not scroll-driven: `currentPage` is the single source of
  truth. Reading scrollLeft back to infer which page we're "on" (the
  previous version) is a continuous value being forced into a discrete
  one — the boundary math is inherently approximate and drifted exactly at
  edges (5th product already scrolled into view, indicator still reading
  02/03). Page number and scroll position now both come FROM currentPage;
  nothing reads scrollLeft to figure out where we are.

  CARDS_PER_SCREEN is hardcoded to match the fixed
  --item-width: calc((100% - var(--gap)) / 2) override in assets/custom.css
  — both sides need to agree on "2 cards per screen" if either changes.

  Desktop only by CSS ([id$="..."] .grid-slider__pagination in
  assets/custom.css); this script still runs on mobile, it just builds a
  hidden element (or nothing at all, if there's only one page).
*/
(function () {
  'use strict';

  var CARDS_PER_SCREEN = 2;
  var SCROLL_DURATION = 450; // ms — target is a visible, unhurried glide

  var TARGET_SELECTOR =
    '[id$="section_collection_Yb3qhH"] grid-slider, ' +
    '[id$="section_collection_4kUEtd"] grid-slider';

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  function initPageIndicator(gridSlider) {
    if (gridSlider.hasAttribute('data-page-indicator-initialized')) return;

    var track = gridSlider.querySelector('[data-grid-slider]');
    var items = gridSlider.querySelectorAll('[data-grid-item]');
    if (!track || !items.length) return;

    gridSlider.setAttribute('data-page-indicator-initialized', '');

    var totalPages = Math.max(1, Math.ceil(items.length / CARDS_PER_SCREEN));

    // Nothing to page through — don't show "01 / 01" with two dead arrows.
    if (totalPages <= 1) return;

    var currentPage = 1;

    var indicator = document.createElement('div');
    indicator.className = 'grid-slider__pagination';
    indicator.innerHTML =
      '<button type="button" class="grid-slider__pagination-arrow" data-pagination-prev aria-label="Previous">←</button>' +
      '<span class="grid-slider__pagination-count">' +
      '<span data-pagination-current>01</span> / <span data-pagination-total>' +
      pad(totalPages) +
      '</span></span>' +
      '<button type="button" class="grid-slider__pagination-arrow" data-pagination-next aria-label="Next">→</button>';
    gridSlider.appendChild(indicator);

    var currentEl = indicator.querySelector('[data-pagination-current]');
    var prevBtn = indicator.querySelector('[data-pagination-prev]');
    var nextBtn = indicator.querySelector('[data-pagination-next]');

    function render() {
      currentEl.textContent = pad(currentPage);
      prevBtn.disabled = currentPage <= 1;
      nextBtn.disabled = currentPage >= totalPages;
    }

    // itemWidth * CARDS_PER_SCREEN undercounts the real step by the gap
    // margin between cards (native .grid-item{margin-right:var(--gap)}),
    // and that undercount compounds every page. Measuring the actual
    // distance between two cards N screens apart
    // (items[N].offsetLeft - items[0].offsetLeft) sidesteps the CSS
    // variable entirely and is exact regardless of what --gap/--outer/
    // border-box rounding actually resolve to. Shared by both directions:
    // currentPage -> scroll position, and scroll position -> currentPage.
    function getScreenStep() {
      return items.length > CARDS_PER_SCREEN
        ? items[CARDS_PER_SCREEN].offsetLeft - items[0].offsetLeft
        : items[0].getBoundingClientRect().width;
    }

    function getMaxScrollLeft() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    // Set for the duration of an arrow-triggered animation (plus a 200ms
    // grace period after it settles) so the scroll listener below can tell
    // "this is our own animateScrollTo re-firing scroll every frame" apart
    // from "the user is dragging". Scoped per-instance (not top-level)
    // because animateScrollTo itself is defined per-instance now, so it
    // can close over this directly instead of threading it through as a
    // shared/global flag across both grid-sliders.
    var isAnimating = false;
    var isAnimatingResetTimer = null;

    // Native scrollTo({behavior:'smooth'}) duration isn't specced/tunable —
    // browsers pick their own (often noticeably faster than 450ms), which
    // is exactly the "刷一下就过去了" complaint. Animating scrollLeft by
    // hand over a fixed duration is the only reliable way to guarantee
    // 400–500ms across browsers.
    function animateScrollTo(targetLeft, duration) {
      var startLeft = track.scrollLeft;
      var distance = targetLeft - startLeft;

      if (distance === 0) return;

      if (isAnimatingResetTimer) window.clearTimeout(isAnimatingResetTimer);
      isAnimating = true;

      track.classList.add('scroll-snap-disabled');
      var startTime = null;

      function step(timestamp) {
        if (startTime === null) startTime = timestamp;
        var elapsed = timestamp - startTime;
        var progress = Math.min(1, elapsed / duration);

        track.scrollLeft = startLeft + distance * easeInOutQuad(progress);

        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          track.classList.remove('scroll-snap-disabled');
          isAnimatingResetTimer = window.setTimeout(function () {
            isAnimating = false;
          }, 200);
        }
      }

      window.requestAnimationFrame(step);
    }

    function scrollToCurrentPage() {
      var screenStep = getScreenStep();

      // Last page (5 products / 2 per screen = page 3) has only one real
      // card — (currentPage-1)*screenStep would overshoot the actual
      // scrollable content, so clamp to the real max scroll distance.
      // currentPage/the displayed number still reads 03/03 regardless.
      var targetLeft = (currentPage - 1) * screenStep;
      targetLeft = Math.max(0, Math.min(getMaxScrollLeft(), targetLeft));

      animateScrollTo(targetLeft, SCROLL_DURATION);
    }

    function goToPage(page) {
      currentPage = Math.max(1, Math.min(totalPages, page));
      render();
      scrollToCurrentPage();
    }

    prevBtn.addEventListener('click', function () {
      goToPage(currentPage - 1);
    });
    nextBtn.addEventListener('click', function () {
      goToPage(currentPage + 1);
    });

    // Manual drag/scroll bypasses goToPage entirely, so currentPage goes
    // stale unless something reads the real scroll position back. This is
    // the ONE place scrollLeft gets read — purely to sync the displayed
    // state to wherever the user's drag left off, never to drive a
    // scroll. It only fires ~150ms after scrolling stops (debounced on
    // "scroll", not scrollend, for broader browser support), so a drag
    // still in motion never flashes intermediate numbers, and it never
    // calls scrollToCurrentPage — arrow clicks are the only thing that
    // moves the track.
    var scrollSyncTimer = null;
    function syncCurrentPageFromScroll() {
      var maxScrollLeft = getMaxScrollLeft();

      // Last page's target position gets clamped to maxScrollLeft in
      // scrollToCurrentPage because there's only 1 real card to scroll to
      // (5 products / 2 per screen), not a full screenStep short of the
      // end. Dividing that clamped scrollLeft by screenStep and rounding
      // lands on totalPages-1, not totalPages — reading the clamp boundary
      // directly instead avoids that rounding-down entirely.
      if (maxScrollLeft > 0 && track.scrollLeft >= maxScrollLeft - 2) {
        if (currentPage !== totalPages) {
          currentPage = totalPages;
          render();
        }
        return;
      }

      var screenStep = getScreenStep();
      if (!screenStep) return;

      var page = Math.round(track.scrollLeft / screenStep) + 1;
      page = Math.max(1, Math.min(totalPages, page));

      if (page !== currentPage) {
        currentPage = page;
        render();
      }
    }

    track.addEventListener('scroll', function () {
      // animateScrollTo mutates track.scrollLeft every frame, which fires
      // "scroll" just like a real drag would — without this check, an
      // arrow-triggered animation re-enters this same sync path mid-flight
      // and, worse, can read the clamped last-page scrollLeft as page
      // totalPages-1 (see syncCurrentPageFromScroll) right as an animation
      // in the OTHER direction is still landing, flashing the wrong number
      // before snapping back.
      if (isAnimating) return;

      if (scrollSyncTimer) window.clearTimeout(scrollSyncTimer);
      scrollSyncTimer = window.setTimeout(syncCurrentPageFromScroll, 150);
    });

    // Card width changes on resize (--item-width is a percentage) — resync
    // the scroll position to whatever page we're already on. currentPage
    // itself doesn't change, so this never touches the displayed number.
    document.addEventListener('theme:resize:width', function () {
      scrollToCurrentPage();
    });

    render();
  }

  function init() {
    document.querySelectorAll(TARGET_SELECTOR).forEach(initPageIndicator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/*
  Mobile sticky cart-bar "Add to cart" fix — theme bug, not ours.

  assets/cart-bar.js's handleClick() proxies the click to the real form's
  submit button via:
    this.form.querySelector("[data-add-to-cart]").dispatchEvent(new Event("click", {bubbles:true}))
  new Event() is untrusted (isTrusted: false); browsers don't run a submit
  button's native click->form-submit activation behavior for it, so no
  request ever leaves (confirmed empty Network panel). The spinner
  (.is-loading, added right before that line) then never clears, since
  nothing downstream ever fires to trigger the cleanup in productAddCallback.

  Fixed here instead of editing assets/cart-bar.js directly (vendor file,
  would conflict on theme updates). Prototype-patching CartBar's
  handleClick wouldn't work: connectedCallback binds it via
  this.addEventListener("click", this.handleClick.bind(this)), and that
  .bind() freezes a reference to whichever implementation exists at
  upgrade time — custom element upgrade happens synchronously inside
  customElements.define(), before any patch from this file could run.

  Intercepting in the capture phase sidesteps that entirely: this listener
  runs before cart-bar's own bubble-phase handler, and
  stopImmediatePropagation() kills that original handler outright, so its
  broken dispatchEvent() line never executes — no double-submit risk.
  Replicates the original's own behavior (preventDefault, is-loading
  class + disabled on the *proxy* button — not the real one, so the
  .click() below isn't blocked by a disabled state we just set), swapping
  only the broken proxy line for a real .click() call, which does trigger
  native activation behavior.
*/
(function () {
  'use strict';

  document.addEventListener(
    'click',
    function (event) {
      var trigger = event.target.closest('[data-cart-bar-add-to-cart]');
      if (!trigger) return;

      var cartBar = trigger.closest('cart-bar');
      if (!cartBar || !cartBar.form) return;

      var realButton = cartBar.form.querySelector('[data-add-to-cart]');
      if (!realButton) return;

      event.stopImmediatePropagation();
      event.preventDefault();

      trigger.classList.add('is-loading');
      trigger.setAttribute('disabled', 'disabled');

      realButton.click();
    },
    true
  );
})();
